'use client';

import { HttpLink, ApolloLink, ObservableQuery } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { useApolloClient } from '@apollo/client/react';
import { ApolloNextAppProvider, ApolloClient, InMemoryCache } from '@apollo/client-integration-nextjs';
import { PropsWithChildren, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import env from 'config/env';
import {
  getStoredAuthMethod,
  AUTH_METHOD,
  getStoredAuthToken,
  AUTH_METHOD_KEY,
} from 'context/Auth/auth-provider.utils';
import { getAccessToken } from 'context/Auth/msal-utils';
import { LessonPackagesResponse } from 'model/LessonPackage';
import { SELF_STUDY_ASSIGNMENT_REPORT_QUERY_NAMES } from 'network/graphql/SelfStudyAssignment';
import { ddlog } from 'utils/miscellaneous';
import { isCypress } from 'utils/testing';

import typePolicies from './type-policies';

const makeClient = (gqlUrl: string) => () => {
  const httpLink = new HttpLink({
    uri: gqlUrl,
  });

  const authLink = new SetContextLink(async ({ headers }) => {
    const authMethod = getStoredAuthMethod();
    let token: string | null = null;
    if (authMethod === AUTH_METHOD.SCHOOL) {
      token = getStoredAuthToken();
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
          [AUTH_METHOD_KEY]: AUTH_METHOD.SCHOOL,
          'x-request-id': uuidv4().toString(),
        },
      };
    } else {
      token = (await getAccessToken()) || null;
      if (!token) {
        ddlog.error('getAccessToken returned empty access token', { authMethod }, undefined);
      }
    }
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
        [AUTH_METHOD_KEY]: AUTH_METHOD.MSAL,
        'x-request-id': uuidv4().toString(),
      },
    };
  });

  return new ApolloClient({
    cache: new InMemoryCache({ typePolicies }),
    link: ApolloLink.from([authLink, httpLink]),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false,
      },
    },
  });
};

const shouldIgnoreQuery = (observableQuery: ObservableQuery, result: unknown) => {
  // Each card's report has its own Refresh and "Last updated"; refetching every card on tab
  // focus would rebuild every report in the class at once.
  if (observableQuery.queryName && SELF_STUDY_ASSIGNMENT_REPORT_QUERY_NAMES.includes(observableQuery.queryName)) {
    return true;
  }

  if (observableQuery.queryName === 'LessonPackages') {
    const pageLimit = observableQuery.options.variables?.filter?.limit;
    const lessonPackagesResult = result as LessonPackagesResponse;
    const numberOfResults = lessonPackagesResult?.lessonPackages?.edges?.length;

    if (numberOfResults > pageLimit) {
      return true;
    }
  }

  return false;
};

export const useRefetchQueriesOnFocus = () => {
  const apolloClient = useApolloClient();
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isCypress()) {
        apolloClient.refetchQueries({
          include: 'active',
          onQueryUpdated: (observableQuery, { result }) => !shouldIgnoreQuery(observableQuery, result),
        });
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [apolloClient]);
};

const RefetchQueriesOnFocus = () => {
  useRefetchQueriesOnFocus();
  return null;
};

export function ApolloWrapper({ children }: PropsWithChildren) {
  return (
    <ApolloNextAppProvider makeClient={makeClient(env.GQL_URL)}>
      <RefetchQueriesOnFocus />
      {children}
    </ApolloNextAppProvider>
  );
}
